import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PrinterIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  BanknotesIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { documentService, paiementService, assistantService, RelanceBrouillon } from '../../api/services';
import {
  Document,
  Paiement,
  TypeDocument,
  ModePaiement,
  TVA_LABELS,
  MODE_PAIEMENT_LABELS,
  StatutDocument,
  STATUT_DOCUMENT_LABELS,
  TYPE_DOCUMENT_LABELS,
} from '../../types';
import { formatTND, formatDate } from '../../utils/formatters';
import { StatusBadge, DocumentTypeBadge } from '../../components/common/StatusBadge';
import { MoneyDisplay } from '../../components/common/MoneyDisplay';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { Modal } from '../../components/common/Modal';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import PaymentLinksSection from '../../components/documents/PaymentLinksSection';
import EcheancierSection from '../../components/documents/EcheancierSection';
import EInvoiceSection from '../../components/documents/EInvoiceSection';
import toast from 'react-hot-toast';

/** TF-002 — cibles de transformation autorisées par type (vente). */
const CONVERT_TARGETS: Partial<Record<TypeDocument, TypeDocument[]>> = {
  [TypeDocument.DEVIS]: [TypeDocument.COMMANDE, TypeDocument.FACTURE],
  [TypeDocument.COMMANDE]: [TypeDocument.BON_LIVRAISON, TypeDocument.FACTURE],
  [TypeDocument.BON_LIVRAISON]: [TypeDocument.FACTURE],
};

export function DocumentDetail() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [document, setDocument] = useState<Document | null>(null);
  const [paiements, setPaiements] = useState<Paiement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [relanceLoading, setRelanceLoading] = useState(false);
  const [relanceDraft, setRelanceDraft] = useState<RelanceBrouillon | null>(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMode, setPaymentMode] = useState<ModePaiement>(ModePaiement.VIREMENT);
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [savingPayment, setSavingPayment] = useState(false);
  const [pdfPreview, setPdfPreview] = useState<{ url: string; lang: 'fr' | 'ar' } | null>(null);

  const openPdfPreview = async (lang: 'fr' | 'ar') => {
    try {
      const blob: Blob = await (await import('../../api/services')).documentService.getPdf(document!.id, lang);
      const url = URL.createObjectURL(blob);
      if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
      setPdfPreview({ url, lang });
    } catch {
      toast.error('Impossible de générer le PDF');
    }
  };

  const closePdfPreview = () => {
    if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url);
    setPdfPreview(null);
  };

  useEffect(() => {
    if (id) loadDocument(parseInt(id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDocument = async (docId: number) => {
    try {
      setLoading(true);
      const [doc, pays] = await Promise.all([
        documentService.getById(docId),
        paiementService.getByDocument(docId),
      ]);
      setDocument(doc);
      setPaiements(pays);
      setPaymentAmount(doc.resteAPayer);
    } catch (error) {
      toast.error(t.messages.loadError);
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToFacture = async () => {
    if (!document) return;
    try {
      const facture = await documentService.convertDevisToFacture(document.id);
      toast.success(t.messages.convertSuccess);
      navigate(`/documents/view/${facture.id}`);
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setShowConvertConfirm(false);
    }
  };

  const handleConvert = async (target: TypeDocument) => {
    if (!document) return;
    try {
      const created = await documentService.convertTo(document.id, target);
      toast.success(`Transformé en ${TYPE_DOCUMENT_LABELS[target]}`);
      navigate(`/documents/view/${created.id}`);
    } catch (error) {
      toast.error(t.messages.saveError);
    }
  };

  const handleAcompte = async () => {
    if (!document) return;
    const saisie = window.prompt("Pourcentage d'acompte à facturer (%) ?", '30');
    if (saisie == null || saisie.trim() === '') return;
    const pct = Number(saisie);
    if (!Number.isFinite(pct) || pct <= 0 || pct > 100) {
      toast.error('Pourcentage invalide.');
      return;
    }
    try {
      const created = await documentService.creerAcompte(document.id, pct);
      toast.success('Facture d’acompte créée');
      navigate(`/documents/view/${created.id}`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t.messages.saveError);
    }
  };

  const handleChangeStatut = async (statut: StatutDocument) => {
    if (!document) return;
    try {
      await documentService.changeStatut(document.id, statut);
      toast.success(`Statut : ${STATUT_DOCUMENT_LABELS[statut]}`);
      loadDocument(document.id);
    } catch (error) {
      toast.error(t.messages.saveError);
    }
  };

  const genererRelance = async () => {
    if (!document) return;
    setRelanceLoading(true);
    try {
      setRelanceDraft(await assistantService.genererRelance(document.id));
    } catch {
      toast.error('Brouillon de relance indisponible.');
    } finally {
      setRelanceLoading(false);
    }
  };

  const handleRecordPayment = async () => {
    if (!document || paymentAmount <= 0) return;
    try {
      setSavingPayment(true);
      await paiementService.create({
        documentId: document.id,
        datePaiement: paymentDate,
        montant: paymentAmount,
        modePaiement: paymentMode,
        reference: paymentReference || undefined,
      });
      toast.success(t.messages.paymentSuccess);
      setShowPaymentModal(false);
      loadDocument(document.id);
    } catch (error) {
      toast.error(t.messages.saveError);
    } finally {
      setSavingPayment(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) return <LoadingSpinner />;
  if (!document) return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 no-print">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="btn-outline p-2">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="page-title">{document.numero}</h1>
            <div className="flex items-center gap-2 mt-1">
              <DocumentTypeBadge type={document.typeDocument} />
              <StatusBadge statut={document.statutPaiement} />
              {document.statut && (
                <span
                  className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                    document.statut === StatutDocument.ACCEPTE || document.statut === StatutDocument.VALIDE
                      ? 'bg-green-100 text-green-800'
                      : document.statut === StatutDocument.REFUSE
                        || document.statut === StatutDocument.EXPIRE
                        || document.statut === StatutDocument.ANNULE
                        ? 'bg-red-100 text-red-800'
                        : document.statut === StatutDocument.ENVOYE
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {STATUT_DOCUMENT_LABELS[document.statut]}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {(document.statutsCibles ?? []).map((cible) => (
            <button
              key={cible}
              onClick={() => handleChangeStatut(cible)}
              className={
                cible === StatutDocument.ACCEPTE || cible === StatutDocument.VALIDE
                  ? 'btn-primary'
                  : cible === StatutDocument.REFUSE
                    || cible === StatutDocument.EXPIRE
                    || cible === StatutDocument.ANNULE
                    ? 'btn-danger'
                    : 'btn-outline'
              }
            >
              {STATUT_DOCUMENT_LABELS[cible]}
            </button>
          ))}
          {document.statut !== StatutDocument.ANNULE
            && document.statut !== StatutDocument.REFUSE
            && document.statut !== StatutDocument.EXPIRE
            && (CONVERT_TARGETS[document.typeDocument] ?? []).map((target) => (
              <button key={target} onClick={() => handleConvert(target)} className="btn-accent">
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                {`Transformer en ${TYPE_DOCUMENT_LABELS[target]}`}
              </button>
            ))}
          {(document.typeDocument === TypeDocument.DEVIS || document.typeDocument === TypeDocument.COMMANDE)
            && document.statut !== StatutDocument.ANNULE
            && document.statut !== StatutDocument.REFUSE
            && document.statut !== StatutDocument.EXPIRE && (
              <button onClick={handleAcompte} className="btn-outline">
                Facturer un acompte
              </button>
            )}
          {document.typeDocument === TypeDocument.FACTURE && document.resteAPayer > 0 && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="btn-primary"
            >
              <BanknotesIcon className="h-4 w-4 mr-1" />
              {t.documents.recordPayment}
            </button>
          )}
          {document.typeDocument === TypeDocument.FACTURE
            && document.sensDocument !== 'ACHAT' && document.resteAPayer > 0 && (
            <button onClick={genererRelance} disabled={relanceLoading} className="btn-outline">
              {relanceLoading ? '…' : '✨ Relance IA'}
            </button>
          )}
          <button
            onClick={() => navigate(
              document.sensDocument === 'ACHAT'
                ? `/stock/achats/${document.id}/edit`
                : `/documents/${document.id}/edit`,
            )}
            className="btn-secondary"
          >
            <PencilSquareIcon className="h-4 w-4 mr-1" />
            {t.common.edit}
          </button>
          <button onClick={() => openPdfPreview('fr')} className="btn-outline">
            <PrinterIcon className="h-4 w-4 mr-1" />
            PDF FR
          </button>
          <button onClick={() => openPdfPreview('ar')} className="btn-outline">
            <PrinterIcon className="h-4 w-4 mr-1" />
            PDF AR
          </button>
          <button onClick={handlePrint} className="btn-outline">
            <PrinterIcon className="h-4 w-4 mr-1" />
            {t.documents.printDocument}
          </button>
        </div>
      </div>

      {pdfPreview && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex flex-col"
          role="dialog"
          aria-label="Aperçu PDF"
          onClick={closePdfPreview}
        >
          <div className="bg-white flex items-center justify-between px-4 py-2 border-b" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold">
              Aperçu PDF — {pdfPreview.lang === 'ar' ? 'العربية' : 'Français'}
            </div>
            <div className="flex items-center gap-2">
              <a href={pdfPreview.url} download={`document-${document?.id}-${pdfPreview.lang}.pdf`} className="btn-secondary">
                Télécharger
              </a>
              <button onClick={closePdfPreview} className="btn-outline">Fermer</button>
            </div>
          </div>
          <iframe
            title="pdf-preview"
            src={pdfPreview.url}
            className="flex-1 w-full bg-white"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">Document</h3>
            <dl className="space-y-1">
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 w-32">{t.documents.numero} :</dt>
                <dd className="text-sm font-medium">{document.numero}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 w-32">{t.documents.dateDocument} :</dt>
                <dd className="text-sm">{formatDate(document.dateDocument)}</dd>
              </div>
              {document.dateEcheance && (
                <div className="flex gap-2">
                  <dt className="text-sm text-gray-500 w-32">{t.documents.dateEcheance} :</dt>
                  <dd className="text-sm">{formatDate(document.dateEcheance)}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="text-sm text-gray-500 w-32">{t.documents.modePaiement} :</dt>
                <dd className="text-sm">{MODE_PAIEMENT_LABELS[document.modePaiement]}</dd>
              </div>
            </dl>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500 mb-2">
              {document.sensDocument === 'ACHAT' ? 'Fournisseur' : t.documents.client}
            </h3>
            <p className="text-sm font-medium text-gray-900">
              {document.sensDocument === 'ACHAT'
                ? (document.fournisseurRaisonSociale ?? document.clientRaisonSociale)
                : document.clientRaisonSociale}
            </p>
            {(document.sensDocument === 'ACHAT' ? document.fournisseurMatriculeFiscal : document.clientMatriculeFiscal) && (
              <p className="text-sm text-gray-500">
                MF: {document.sensDocument === 'ACHAT' ? document.fournisseurMatriculeFiscal : document.clientMatriculeFiscal}
              </p>
            )}
            {document.sensDocument === 'ACHAT' && document.numeroFournisseur && (
              <p className="text-sm text-gray-500">N° fournisseur: {document.numeroFournisseur}</p>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t.documents.article}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t.documents.quantite}</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.documents.prixUnitaire}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">{t.documents.remise}</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">TVA</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.documents.montantHT}</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.documents.montantFodec}</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t.documents.montantTva}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {document.lignes.map((ligne, idx) => (
                <tr key={ligne.id || idx}>
                  <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {ligne.articleDesignation}
                    </div>
                    {ligne.articleCode && (
                      <div className="text-xs text-gray-500">{ligne.articleCode}</div>
                    )}
                    {document.typeDocument === TypeDocument.COMMANDE && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        Livré {ligne.quantiteLivree ?? 0}/{ligne.quantite}
                        {' · '}Facturé {ligne.quantiteFacturee ?? 0}/{ligne.quantite}
                        {(ligne.reliquatLivraison ?? 0) > 0 && (
                          <span className="text-amber-600"> · reliquat {ligne.reliquatLivraison}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">{ligne.quantite}</td>
                  <td className="px-4 py-3 text-right text-sm tnd-amount">
                    {formatTND(ligne.prixUnitaireHT)}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">
                    {ligne.remise > 0 ? `${Math.round(ligne.remise)}%` : '-'}
                  </td>
                  <td className="px-4 py-3 text-center text-sm">{TVA_LABELS[ligne.tauxTva]}</td>
                  <td className="px-4 py-3 text-right text-sm tnd-amount font-medium">
                    {formatTND(ligne.montantHT)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tnd-amount">
                    {formatTND(ligne.montantFodec)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm tnd-amount">
                    {formatTND(ligne.montantTva)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 flex justify-end">
          <div className="w-full max-w-sm space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t.documents.totalHT}</span>
              <MoneyDisplay amount={document.totalHT} />
            </div>
            {document.totalFodec > 0 && (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.documents.totalFodec}</span>
                <MoneyDisplay amount={document.totalFodec} />
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t.documents.totalTva}</span>
              <MoneyDisplay amount={document.totalTva} />
            </div>
            <div className="border-t pt-2 flex justify-between text-sm font-medium">
              <span>{t.documents.totalTTC}</span>
              <MoneyDisplay amount={document.totalTTC} />
            </div>
            {document.timbreFiscal > 0 ? (
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">{t.documents.timbreFiscal}</span>
                <MoneyDisplay amount={document.timbreFiscal} />
              </div>
            ) : document.appliquerTimbreFiscal === false && (
              // Exonération explicite : on le dit, plutôt que de laisser une ligne absente
              // qui ressemblerait à un oubli de saisie.
              <div className="flex justify-between text-sm text-gray-400">
                <span>{t.documents.timbreFiscal}</span>
                <span>{t.documents.timbreFiscalNonApplique}</span>
              </div>
            )}
            {document.retenueSource > 0 && (
              <div className="flex justify-between text-sm text-red-600">
                <span>{t.documents.retenueSource}</span>
                <span>- {formatTND(document.retenueSource)}</span>
              </div>
            )}
            <div className="border-t-2 border-primary-500 pt-3 flex justify-between">
              <span className="text-lg font-bold">{t.documents.netAPayer}</span>
              <MoneyDisplay amount={document.netAPayer} size="lg" className="text-primary-600" />
            </div>
            {document.co2eKg != null && document.co2eKg > 0 && (
              <div className="mt-3 flex items-center justify-between rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2">
                <span className="flex items-center gap-2 text-sm text-emerald-800">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                    <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3c.5.12 1 .2 1.5.2C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
                  </svg>
                  Empreinte carbone
                </span>
                <span className="text-sm font-semibold text-emerald-900">
                  {document.co2eKg.toLocaleString('fr-TN', { maximumFractionDigits: 2 })} kg CO₂e
                </span>
              </div>
            )}
            {document.typeDocument === TypeDocument.FACTURE && (
              <>
                <div className="flex justify-between text-sm text-green-600">
                  <span>{t.documents.montantPaye}</span>
                  <span>{formatTND(document.montantPaye)}</span>
                </div>
                <div className="flex justify-between text-sm font-medium text-orange-600">
                  <span>{t.documents.resteAPayer}</span>
                  <span>{formatTND(document.resteAPayer)}</span>
                </div>
              </>
            )}
          </div>
        </div>

        {document.notes && (
          <div className="mt-6 border-t pt-4">
            <h3 className="text-sm font-medium text-gray-500 mb-1">{t.common.notes}</h3>
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{document.notes}</p>
          </div>
        )}
      </div>

      {document.typeDocument === TypeDocument.FACTURE && (
        <PaymentLinksSection documentId={document.id} className="no-print" />
      )}

      {document.typeDocument === TypeDocument.FACTURE && (
        <EcheancierSection
          documentId={document.id}
          className="no-print"
          onChanged={() => loadDocument(document.id)}
        />
      )}

      {document.factureElectronique && (
        <EInvoiceSection
          document={document}
          className="no-print"
          onChanged={() => loadDocument(document.id)}
        />
      )}

      {paiements.length > 0 && (
        <div className="card no-print">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t.documents.paymentHistory}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t.common.date}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    {t.common.amount}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t.paiements.modePaiement}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t.common.reference}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paiements.map((p) => (
                  <tr key={p.id}>
                    <td className="px-4 py-3 text-sm">{formatDate(p.datePaiement)}</td>
                    <td className="px-4 py-3 text-right">
                      <MoneyDisplay amount={p.montant} size="sm" />
                    </td>
                    <td className="px-4 py-3 text-sm">{MODE_PAIEMENT_LABELS[p.modePaiement]}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{p.reference || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={relanceDraft !== null}
        onClose={() => setRelanceDraft(null)}
        title={`Brouillon de relance${relanceDraft ? ` (rappel ${relanceDraft.palier})` : ''}`}
      >
        {relanceDraft && (
          <div className="space-y-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Objet</label>
              <input readOnly value={relanceDraft.sujet}
                     className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Message</label>
              <textarea readOnly value={relanceDraft.corps} rows={12}
                        className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-gray-50 font-mono" />
            </div>
            <p className="text-xs text-gray-400">
              {relanceDraft.genereParIa
                ? 'Proposé par l’assistant — relisez avant d’envoyer.'
                : 'Modèle déterministe (assistant indisponible) — relisez avant d’envoyer.'}
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(`${relanceDraft.sujet}\n\n${relanceDraft.corps}`)
                    .then(() => toast.success('Relance copiée')).catch(() => {});
                }}
                className="btn-primary"
              >
                Copier
              </button>
              <button onClick={() => setRelanceDraft(null)} className="btn-outline">Fermer</button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={t.documents.recordPayment}
      >
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t.documents.resteAPayer}</span>
              <MoneyDisplay amount={document.resteAPayer} className="text-orange-600 font-bold" />
            </div>
          </div>

          <div>
            <label className="label">{t.paiements.datePaiement} *</label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label className="label">{t.paiements.montant} *</label>
            <div className="relative">
              <input
                type="number"
                step="0.001"
                min="0.001"
                max={document.resteAPayer}
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                className="input-field pr-16"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                TND
              </span>
            </div>
          </div>

          <div>
            <label className="label">{t.paiements.modePaiement} *</label>
            <select
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as ModePaiement)}
              className="input-field"
            >
              {Object.entries(MODE_PAIEMENT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="label">{t.paiements.reference}</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              className="input-field"
              placeholder="N° chèque, référence virement..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowPaymentModal(false)}
              className="btn-outline"
            >
              {t.common.cancel}
            </button>
            <button
              type="button"
              onClick={handleRecordPayment}
              disabled={savingPayment || paymentAmount <= 0}
              className="btn-primary"
            >
              {savingPayment ? t.common.loading : t.common.save}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showConvertConfirm}
        title={t.documents.convertToFacture}
        message={t.documents.convertConfirm}
        variant="info"
        confirmLabel={t.common.confirm}
        onConfirm={handleConvertToFacture}
        onCancel={() => setShowConvertConfirm(false)}
      />
    </div>
  );
}

export default DocumentDetail;
